<template>
  <Transition name="codex-fade">
    <div
      v-if="visible"
      class="codex-backdrop"
      @click.self="emit('close')"
      @keydown.esc="emit('close')"
      role="dialog"
      aria-modal="true"
      :aria-label="STRINGS.tutorial.codex.title"
    >
      <div class="codex-panel">
        <!-- Header -->
        <div class="codex-header">
          <span class="codex-title font-display">{{ STRINGS.tutorial.codex.title }}</span>
          <button
            class="codex-close"
            @click="emit('close')"
            :aria-label="STRINGS.tutorial.codex.close"
          >
            <AppIcon name="close-x" :size="16" />
          </button>
        </div>

        <!-- Welcome banner (first-time users) -->
        <div v-if="showWelcome" class="codex-welcome">
          <p class="codex-welcome-heading font-display">{{ STRINGS.tutorial.codex.welcome }}</p>
          <p class="codex-welcome-body">{{ STRINGS.tutorial.codex.welcomeBody }}</p>
        </div>

        <!-- Tabs -->
        <div class="codex-tabs" role="tablist">
          <button
            v-for="(tab, i) in tabs"
            :key="tab.key"
            class="codex-tab"
            :class="{
              'codex-tab--active': activeTab === i,
              'codex-tab--new': hasNewRule && tab.key === 'scoring',
            }"
            role="tab"
            :aria-selected="activeTab === i"
            @click="activeTab = i"
          >
            {{ tab.label }}
            <span v-if="hasNewRule && tab.key === 'scoring'" class="codex-new-badge">
              {{ STRINGS.tutorial.codex.newBadge }}
            </span>
          </button>
        </div>

        <!-- Tab content -->
        <div class="codex-content" role="tabpanel">
          <!-- How It Works -->
          <template v-if="activeTab === 0">
            <div
              v-for="(section, i) in STRINGS.tutorial.codex.howItWorks"
              :key="i"
              class="codex-section"
            >
              <h3 class="codex-section-heading">{{ section.heading }}</h3>
              <ul class="codex-bullets">
                <li v-for="bullet in section.bullets" :key="bullet">{{ bullet }}</li>
              </ul>
            </div>
          </template>

          <!-- Letters -->
          <template v-else-if="activeTab === 1">
            <div
              v-for="(section, i) in STRINGS.tutorial.codex.letters"
              :key="i"
              class="codex-section"
            >
              <h3 class="codex-section-heading">{{ section.heading }}</h3>
              <ul class="codex-bullets">
                <li v-for="bullet in section.bullets" :key="bullet">{{ bullet }}</li>
              </ul>
            </div>
          </template>

          <!-- Scoring -->
          <template v-else-if="activeTab === 2">
            <div
              v-for="(section, i) in STRINGS.tutorial.codex.scoring"
              :key="i"
              class="codex-section"
              :class="{ 'codex-section--highlighted': hasNewRule && section.isRareRule }"
            >
              <h3 class="codex-section-heading">{{ section.heading }}</h3>
              <ul class="codex-bullets">
                <li
                  v-for="bullet in section.bullets"
                  :key="bullet"
                  :class="{
                    'codex-bullet--new':
                      hasNewRule && section.isRareRule && bullet.includes('rare'),
                  }"
                >
                  {{ bullet }}
                  <span
                    v-if="hasNewRule && section.isRareRule && bullet.includes('rare')"
                    class="codex-new-badge codex-new-badge--inline"
                  >
                    {{ STRINGS.tutorial.codex.newBadge }}
                  </span>
                </li>
              </ul>
            </div>
          </template>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import STRINGS from '@/constants/strings';
import AppIcon from '@/components/ui/icons/AppIcon.vue';

interface Props {
  visible: boolean;
  showWelcome: boolean;
  hasNewRule: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
  close: [];
}>();

const tabs = [
  { key: 'howItWorks', label: STRINGS.tutorial.codex.tabs.howItWorks },
  { key: 'letters', label: STRINGS.tutorial.codex.tabs.letters },
  { key: 'scoring', label: STRINGS.tutorial.codex.tabs.scoring },
];

const activeTab = ref(0);
</script>

<style scoped>
.codex-backdrop {
  position: fixed;
  inset: 0;
  z-index: 8900;
  background: rgba(10, 10, 15, 0.6);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

@media (min-width: 768px) {
  .codex-backdrop {
    align-items: flex-start;
    justify-content: flex-end;
    padding: 5rem 1rem 1rem;
  }
}

.codex-panel {
  background: var(--color-arena-surface, #1a1612);
  border: 2px solid var(--color-arena-border, #3d3428);
  border-bottom: none;
  border-radius: 1rem 1rem 0 0;
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.5);
}

@media (min-width: 768px) {
  .codex-panel {
    border: 2px solid var(--color-arena-border, #3d3428);
    border-radius: 1rem;
    max-height: 70vh;
    width: 360px;
  }
}

.codex-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem 0.75rem;
  border-bottom: 1px solid var(--color-arena-border, #3d3428);
  flex-shrink: 0;
}

.codex-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--color-energy-primary, #c9a84c);
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.codex-close {
  background: transparent;
  border: none;
  color: var(--color-arena-text-muted, #8a7e6a);
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: color 150ms;
  line-height: 1;
}

.codex-close:hover {
  color: var(--color-arena-text-bright, #f5e6c8);
}

.codex-welcome {
  padding: 0.75rem 1.25rem;
  background: rgba(201, 168, 76, 0.06);
  border-bottom: 1px solid rgba(201, 168, 76, 0.15);
  flex-shrink: 0;
}

.codex-welcome-heading {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--color-energy-primary, #c9a84c);
  margin: 0 0 0.25rem;
  letter-spacing: 0.05em;
}

.codex-welcome-body {
  font-size: 0.8rem;
  color: var(--color-arena-text-muted, #8a7e6a);
  margin: 0;
  line-height: 1.5;
}

.codex-tabs {
  display: flex;
  border-bottom: 1px solid var(--color-arena-border, #3d3428);
  flex-shrink: 0;
}

.codex-tab {
  flex: 1;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--color-arena-text-muted, #8a7e6a);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.6rem 0.5rem;
  transition:
    color 150ms,
    border-color 150ms;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  margin-bottom: -1px;
}

.codex-tab--active {
  color: var(--color-arena-text-bright, #f5e6c8);
  border-bottom-color: var(--color-energy-primary, #c9a84c);
}

.codex-tab:hover:not(.codex-tab--active) {
  color: var(--color-arena-text-bright, #f5e6c8);
}

.codex-new-badge {
  display: inline-block;
  background: var(--color-energy-primary, #c9a84c);
  color: #1a1612;
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.1rem 0.3rem;
  border-radius: 3px;
  line-height: 1.4;
}

.codex-new-badge--inline {
  vertical-align: middle;
  margin-left: 0.25rem;
}

.codex-content {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.codex-section {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.codex-section--highlighted {
  background: rgba(201, 168, 76, 0.05);
  border: 1px solid rgba(201, 168, 76, 0.15);
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin: -0.25rem;
}

.codex-section-heading {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--color-arena-text-bright, #f5e6c8);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.codex-bullets {
  margin: 0;
  padding: 0 0 0 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.codex-bullets li {
  font-size: 0.8rem;
  color: var(--color-arena-text-muted, #8a7e6a);
  line-height: 1.5;
}

.codex-bullet--new {
  color: var(--color-arena-text-bright, #f5e6c8);
}

/* Transitions */
.codex-fade-enter-active,
.codex-fade-leave-active {
  transition: opacity 200ms ease;
}

.codex-fade-enter-active .codex-panel,
.codex-fade-leave-active .codex-panel {
  transition: transform 200ms ease;
}

.codex-fade-enter-from,
.codex-fade-leave-to {
  opacity: 0;
}

.codex-fade-enter-from .codex-panel {
  transform: translateY(20px);
}

.codex-fade-leave-to .codex-panel {
  transform: translateY(20px);
}

@media (min-width: 768px) {
  .codex-fade-enter-from .codex-panel {
    transform: translateX(20px);
  }
  .codex-fade-leave-to .codex-panel {
    transform: translateX(20px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .codex-fade-enter-active,
  .codex-fade-leave-active {
    transition: none;
  }
  .codex-fade-enter-active .codex-panel,
  .codex-fade-leave-active .codex-panel {
    transition: none;
  }
}
</style>
